import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  FaArrowLeft,
  FaChevronLeft,
  FaChevronRight,
  FaExternalLinkAlt,
  FaMapMarkerAlt,
  FaTimes,
  FaWhatsapp,
} from "react-icons/fa";

// Map bundled images under src/image_center (webpack require.context)
const imageCenterMap = (() => {
  try {
    const req = require.context(
      "../../image_center",
      false,
      /\.(png|jpe?g|webp|svg|gif)$/i
    );
    const m = {};
    req.keys().forEach((k) => {
      const key = k.replace(/^\.\//, "");
      const mod = req(k);
      m[key] = mod && mod.default ? mod.default : mod;
    });
    return m;
  } catch (e) {
    return {};
  }
})();

function TutorCenterDetail({ center, addresses, onBack }) {
  const { t } = useTranslation();

  const getAddress = (center) => {
    // Show raw address field when available. No extraction/lookup.
    if (center && center.address) return center.address;
    return "Address not available";
  };

  // getCenterKey is intentionally omitted here (used in list component only)

  // Resolve image sources (same logic as list): support full URLs, absolute paths,
  // attempts to require from src/image_center, and falls back to public/image_center
  const resolveImageSrc = (img) => {
    if (!img) return null;
    const asString = String(img);
    if (/^data:|^https?:\/\//i.test(asString)) return asString;
    if (/^\//.test(asString)) return asString;
    if (imageCenterMap[asString]) return imageCenterMap[asString];
    return (process.env.PUBLIC_URL || "") + `/image_center/${asString}`;
  };

  // Reusable ImageGallery for the detail view
  function ImageGallery({ images = [], alt }) {
    const [idx, setIdx] = useState(0);
    const [isFull, setIsFull] = useState(false);
    const valid = (images || []).filter(Boolean);
    const resolved = valid.map((it) => resolveImageSrc(it)).filter(Boolean);
    const current = resolved[idx] || resolved[0];

    // keyboard navigation for fullscreen
    useEffect(() => {
      if (!isFull) return;
      const onKey = (e) => {
        if (e.key === "Escape") return setIsFull(false);
        if (e.key === "ArrowLeft")
          return setIdx((i) => (i === 0 ? resolved.length - 1 : i - 1));
        if (e.key === "ArrowRight")
          return setIdx((i) => (i === resolved.length - 1 ? 0 : i + 1));
      };
      window.addEventListener("keydown", onKey);
      return () => window.removeEventListener("keydown", onKey);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFull, resolved.length]);

    const prev = (e) => {
      e.stopPropagation();
      setIdx((i) => (i === 0 ? resolved.length - 1 : i - 1));
    };
    const next = (e) => {
      e.stopPropagation();
      setIdx((i) => (i === resolved.length - 1 ? 0 : i + 1));
    };

    const openFull = (i) => {
      setIdx(i || 0);
      setIsFull(true);
    };
    const closeFull = () => setIsFull(false);

    // keyboard navigation for fullscreen
    useEffect(() => {
      if (!isFull) return;
      const onKey = (e) => {
        if (e.key === "Escape") return closeFull();
        if (e.key === "ArrowLeft")
          return setIdx((i) => (i === 0 ? resolved.length - 1 : i - 1));
        if (e.key === "ArrowRight")
          return setIdx((i) => (i === resolved.length - 1 ? 0 : i + 1));
      };
      window.addEventListener("keydown", onKey);
      return () => window.removeEventListener("keydown", onKey);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFull, resolved.length]);

    return (
      <div>
        <div className="relative bg-gray-200">
          <div className="w-full h-96 flex items-center justify-center bg-gray-200">
            <img
              src={current}
              alt={alt}
              style={{ cursor: "pointer" }}
              onClick={() => openFull(idx)}
              className="max-w-full max-h-full object-contain"
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
          </div>

          {resolved.length > 1 && (
            <>
              <button
                onClick={prev}
                className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-75 text-white p-3 rounded-full"
              >
                <FaChevronLeft />
              </button>
              <button
                onClick={next}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-75 text-white p-3 rounded-full"
              >
                <FaChevronRight />
              </button>
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-50 text-white px-3 py-1 rounded-full text-sm">
                {idx + 1} / {resolved.length}
              </div>
            </>
          )}
        </div>

        {/* Thumbnails as squares */}
        <div className="mt-4 flex flex-wrap gap-3">
          {resolved.map((src, i) => (
            <div
              key={i}
              onClick={() => openFull(i)}
              className={`w-24 h-24 sm:w-32 sm:h-32 rounded-md overflow-hidden cursor-pointer border-2 ${
                i === idx ? "border-orange-500" : "border-transparent"
              }`}
            >
              <img
                src={src}
                alt={`${alt} ${i + 1}`}
                className="w-full h-full object-cover"
              />
            </div>
          ))}
        </div>

        {/* Fullscreen overlay */}
        {isFull && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-80 p-4"
            onClick={closeFull}
          >
            <button
              onClick={(e) => {
                e.stopPropagation();
                closeFull();
              }}
              aria-label="Close"
              className="absolute top-4 right-4 text-white text-2xl p-2 bg-black bg-opacity-30 rounded-full"
            >
              <FaTimes />
            </button>

            <button
              onClick={(e) => {
                e.stopPropagation();
                setIdx((i) => (i === 0 ? resolved.length - 1 : i - 1));
              }}
              className="absolute left-4 text-white text-3xl p-3 bg-black bg-opacity-30 rounded-full"
            >
              <FaChevronLeft />
            </button>

            <div className="max-w-full max-h-full flex items-center justify-center">
              <img
                src={resolved[idx]}
                alt={alt}
                className="max-w-full max-h-[90vh] object-contain"
              />
            </div>

            <button
              onClick={(e) => {
                e.stopPropagation();
                setIdx((i) => (i === resolved.length - 1 ? 0 : i + 1));
              }}
              className="absolute right-4 text-white text-3xl p-3 bg-black bg-opacity-30 rounded-full"
            >
              <FaChevronRight />
            </button>

            <div className="absolute bottom-6 text-white text-sm">
              {idx + 1} / {resolved.length}
            </div>
          </div>
        )}
      </div>
    );
  }

  const getGoogleMapsUrl = (address) => {
    const searchAddress = address || getAddress(center);
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
      searchAddress + " Hong Kong"
    )}`;
  };

  const extractCourses = (contentText) => {
    // Extract courses from content_text (lines starting with ⭐)
    const lines = contentText?.split("\n") || [];
    return lines
      .filter((line) => line.trim().startsWith("⭐"))
      .map((line) => line.trim().replace(/^⭐+\s*/, ""));
  };

  // Image navigation handled by ImageGallery component below

  const handleWhatsAppClick = () => {
    if (!center.whatsapp_number) return;
    const normalizeWhatsAppNumber = (num) => {
      if (!num) return "";
      const cleaned = num.replace(/\D/g, "");
      if (!cleaned) return "";
      if (cleaned.length === 8) return `852${cleaned}`;
      const withoutLeadingZeros = cleaned.replace(/^0+/, "");
      if (withoutLeadingZeros.length === 8) return `852${withoutLeadingZeros}`;
      return cleaned;
    };

    const isMobileDevice = () => {
      try {
        return /Mobi|Android|iPhone|iPad|iPod|Windows Phone/i.test(
          navigator.userAgent
        );
      } catch (e) {
        return false;
      }
    };

    const phoneNumber = normalizeWhatsAppNumber(center.whatsapp_number);
    if (!phoneNumber) return;
    const message = `${t("tutorCenters.contactMessage", {
      title: center.title || "",
    })}`;
    const encodedMessage = encodeURIComponent(message);
    let whatsappUrl2 = "";
    if (isMobileDevice()) {
      console.log("Mobile device detected");
      whatsappUrl2 = `https://api.whatsapp.com/send?phone=${phoneNumber}${
        encodedMessage ? `&text=${encodedMessage}` : ""
      }`;
    } else {
      console.log("Desktop device detected");
      whatsappUrl2 = `https://web.whatsapp.com/send?phone=${phoneNumber}${
        encodedMessage ? `&text=${encodedMessage}` : ""
      }`;
    }
    try {
      window.open(whatsappUrl2, "_whatsapp");
    } catch (e) {
      window.location.href = whatsappUrl2;
    }
  };

  const courses = extractCourses(center.content_text);
  const address = getAddress(center);

  const handleBack = () => {
    // Check if we can go back in history
    if (window.history.length > 1) {
      // If there's history, use onBack (navigate(-1))
      if (typeof onBack === "function") {
        onBack();
      } else {
        window.history.back();
      }
    } else {
      // If opened in new tab with no history, close tab or navigate to centers list
      try {
        window.close();
        // If window.close() doesn't work (some browsers block it), navigate to centers page
        setTimeout(() => {
          window.location.href = "/tutorCenters";
        }, 100);
      } catch (e) {
        window.location.href = "/tutorCenters";
      }
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg overflow-hidden">
      {/* Back Button */}
      <div className="p-4 border-b">
        <button
          onClick={handleBack}
          className="flex items-center text-orange-600 hover:text-orange-700 font-medium"
        >
          <FaArrowLeft className="mr-2" />
          {t("back")}
        </button>
      </div>

      {/* Content */}
      <div className="p-6">
        {/* Title */}
        <h1 className="text-3xl font-bold text-gray-800 mb-4">
          {center.title || "Unnamed Center"}
        </h1>
        {/* Image Gallery */}

        {center.images && center.images.length > 0 && center.images[0] && (
          <ImageGallery
            images={center.images}
            alt={center.title || "Tutor Center"}
          />
        )}

        {/* Contact Information */}
        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-3">
            {t("tutorCenters.contactInformation")}
          </h2>

          {/* Address with Google Maps Link */}
          <div className="mb-3">
            <div className="flex items-start text-gray-700 mb-2">
              <FaMapMarkerAlt className="mt-1 mr-3 flex-shrink-0 text-orange-500" />
              <span className="flex-1">{address}</span>
            </div>
            <a
              href={getGoogleMapsUrl(address)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center ml-8 text-blue-600 hover:text-blue-800 text-sm font-medium"
            >
              <FaExternalLinkAlt className="mr-2" />
              {t("tutorCenters.viewOnGoogleMaps")}
            </a>
          </div>

          {/* WhatsApp */}
          {center.whatsapp_number && (
            <div className="mb-3">
              <button
                onClick={handleWhatsAppClick}
                className="flex items-center text-green-600 hover:text-green-700 font-medium"
              >
                <FaWhatsapp className="mr-3 text-xl" />
                <span>{center.whatsapp_number}</span>
              </button>
            </div>
          )}

          {/* Website */}
          {center.url && (
            <div>
              <a
                href={center.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center text-blue-600 hover:text-blue-800 font-medium"
              >
                <FaExternalLinkAlt className="mr-3" />
                Visit Website
              </a>
            </div>
          )}
        </div>

        {/* Google Maps Embed */}
        {address && address !== "Address not available" && (
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-3">
              {t("tutorCenters.locationMap")}
            </h2>
            <div className="w-full h-96 rounded-lg overflow-hidden border border-gray-300">
              <iframe
                width="100%"
                height="100%"
                frameBorder="0"
                style={{ border: 0 }}
                src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${encodeURIComponent(
                  address + ", Hong Kong"
                )}`}
                allowFullScreen
                title="Center Location"
              ></iframe>
            </div>
            <p className="text-sm text-gray-500 mt-2">
              {t("tutorCenters.mapNote")}
            </p>
          </div>
        )}

        {/* Courses Offered */}
        {courses.length > 0 && (
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-3">
              {t("tutorCenters.coursesOffered")}
            </h2>
            <ul className="space-y-2">
              {courses.map((course, index) => (
                <li
                  key={index}
                  className="flex items-start text-gray-700 bg-orange-50 p-3 rounded-lg"
                >
                  <span className="text-orange-500 mr-3">⭐</span>
                  <span>{course}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Tags */}
        {center.product_tags && center.product_tags.length > 0 && (
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-3">
              {t("tutorCenters.tags")}
            </h2>
            <div className="flex flex-wrap gap-2">
              {center.product_tags.map((tag, index) => (
                <span
                  key={index}
                  className="px-3 py-1 bg-orange-100 text-orange-700 text-sm rounded-full"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Full Description */}
        {center.content_text && (
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-3">
              {t("tutorCenters.aboutCenter")}
            </h2>
            <div className="text-gray-700 whitespace-pre-wrap bg-gray-50 p-4 rounded-lg">
              {center.content_text}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 mt-8">
          {center.whatsapp_number && (
            <button
              onClick={handleWhatsAppClick}
              className="flex-1 flex items-center justify-center py-3 px-6 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition-colors duration-200"
            >
              <FaWhatsapp className="mr-2 text-xl" />
              {t("contactWhatsapp")}
            </button>
          )}
          {center.url && (
            <a
              href={center.url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 flex items-center justify-center py-3 px-6 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors duration-200"
            >
              <FaExternalLinkAlt className="mr-2" />
              {t("view")}
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

export default TutorCenterDetail;
